import {
    calculateBatteryLevelFromInternalVoltage,
    calculateBatteryPercentageFromExternalVoltage,
    computeInternalBatteryVoltageFromLevel
} from "components/device-details/v2/compute-batteries";
import {EDeviceCommandTypes} from "contracts/EDeviceCommandTypes";
import {EGeofencingLogic, getDeviceGeofencingLogic} from "contracts/EGeofencingLogic";
import {EHolotrakDeviceFeatures} from "contracts/EHolotrakDeviceFeatures";
import {ELocationDeterminant, getLocationDeterminantFromString} from "contracts/ELocationDeterminant";
import {ESensorType, getSensorTypeFromString} from "contracts/ESensorType";
import {convertStringToTemperatureUnit, ETemperatureUnit} from "contracts/ETemperatureUnit";
import {getCompositeItemSuffix, getCompositeItemTitle} from "contracts/holotrak/_helpers/compositeItemHelpers";
import {formatBatteryValues, IBatteryValues, IFormattedBatteryValues} from "contracts/IBatteryValues";
import {ICANDetail} from "contracts/ICANDetail";
import {IReportableItem} from "contracts/IReportableItem";
import {StateComputedValue, SupportedComputableState} from "contracts/supported-computable-state";
import dayjs from "dayjs";
import {jsonArrayMember, jsonMember, jsonObject, TypedJSON} from "typedjson";
import {EBatteryLevel} from "../EBatteryLevel";
import {EDeviceSignal, EDeviceStatus, ELosantDeviceClass} from "../EDeviceStatus";
import {IDeviceStatus} from "../IDeviceStatus";
import {IHasCompositeState} from "../IHasCompositeState";
import {IHasAttributes, IHasTags} from "../IHasTags";
import {CompositeState, CompositeStateParser, CompositeStateType} from "./compositeState";
import {CompositeStateItem, CompositeStateItemSerializers} from "./compositeStateItem";
import {ConnectionInfo} from "./connectionInfo";
import {DeviceAttribute, DeviceAttributeSerializer, DeviceTag, DeviceTagSerializer} from "./deviceTag";
import {GeofenceSlotConfig} from "./GeofenceSlotConfig";
import {MqttMessage} from "./mqttMessage";

type SupportedDeviceModel = 'barra-gps' | 'holotrak-fill-sensor';

@jsonObject()
export class LosantCoreDevice implements IHasTags, IHasCompositeState, IReportableItem {

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    creationDate: dayjs.Dayjs;

    @jsonMember(String)
    name: string;

    @jsonMember(() => ELosantDeviceClass, {
        deserializer: (value: any) => {
            switch (value) {
                case 'gateway':
                case ELosantDeviceClass.GATEWAY:
                    return ELosantDeviceClass.GATEWAY;

                case 'floating':
                case ELosantDeviceClass.FLOATING:
                    return ELosantDeviceClass.FLOATING;

                case 'device':
                default:
                    return ELosantDeviceClass.DEVICE;
            }
        }
    })
    deviceClass: ELosantDeviceClass;

    @jsonArrayMember(DeviceTag, {
        deserializer: (value: any) => {
            return LosantCoreDevice.parseTags({tags: value});
        }
    })
    tags: DeviceTag[] | string;

    @jsonArrayMember(DeviceAttribute, {
        deserializer: (value: any) => {
            return (!!value) ? LosantCoreDevice.parseAttributes({attributes: value}) : value;
        }
    })
    attributes: DeviceAttribute[];

    @jsonMember(String)
    applicationId: string
    @jsonMember(String)
    _etag: string
    @jsonMember(String)
    deviceId: string
    @jsonMember(String)
    id: string
    @jsonMember(ConnectionInfo)
    connectionInfo: ConnectionInfo
    @jsonMember(CompositeState)
    compositeState: CompositeState;
    @jsonMember(String)
    group: string;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    lastUpdated: dayjs.Dayjs;


    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    updatedAt: dayjs.Dayjs;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    createdAt: dayjs.Dayjs;

    get type(): string {
        return this.getTagValue('device_type').toLowerCase();
    }

    // also treated as IMEI number
    get mobileId(): string {
        return this.getTagValue('mobileId');
    }

    get additionalInfo(): string {
        return this.getCompositeValue('address', '', '', false);
    }

    get uiHeight(): number {
        return (this.additionalInfo || this.additionalInfo === '') ? 111 : 84;
    }

    get status(): EDeviceStatus {
        if (this.sensorType === ESensorType.ALARMS) {
            return this.compositeState?.computeAlarmStatus() ?? EDeviceStatus.NEVER_CONNECTED;
        } else {
            return this.compositeState?.computeOutdoorStatus() ?? EDeviceStatus.NEVER_CONNECTED;
        }
    }

    get signalStrength(): EDeviceSignal {
        return this.compositeState.computeSignalStrength() ?? EDeviceSignal.NONE;
    }

    get statusInfo(): IDeviceStatus {
        let status = this.status;
        if (this.sensorType === ESensorType.ALARMS) {
            status = this.isAlarmArmed ? EDeviceStatus.ALARM_ARMED : EDeviceStatus.ALARM_DISARMED;
        }

        return {
            icon: 'fa fa-circle',
            updatedText: this.lastUpdatedOn?.fromNow() || '--',
            value: status
        }
    }

    get lastUpdatedOn(): dayjs.Dayjs {
        // FIXME: never connected should be never connected.
        return this.getCompositeStateItem('last_reported_at')?.updatedAt ?? this.lastUpdated ?? null;
    }

    get batteryText(): string {
        return this.hasBatteryValues() ? this.getDefiniteBatteryValues().formattedPercentage : 'NA';
    }

    get batteryLevel(): EBatteryLevel {
        if (this.hasBatteryValues()) {
            const batteryLevel = this.getDefiniteBatteryValues().percentage;
            if (batteryLevel >= 90) {
                return EBatteryLevel.FULL;
            }
            if (batteryLevel >= 20 && batteryLevel < 90) {
                return EBatteryLevel.LOW;
            }
            if (batteryLevel < 20) {
                return EBatteryLevel.WARNING;
            }
        }

        return EBatteryLevel.NOT_CONNECTED;
    }

    get sensorType(): ESensorType {
        return getSensorTypeFromString(this.getTagValue('sensor_type'));
    }

    get isMappable(): boolean {
        return true;
    }

    get effectiveDeviceId(): string {
        return this.deviceId;
    }

    get deviceModel(): string {
        return this.getTagValue('device_model');
    }

    get isAlarmArmed(): boolean {
        return this.getCompositeBooleanValue('is_alarm_armed');
    }

    get isAttributeConfigSupported(): boolean {
        return LosantCoreDevice.getAttributeConfigSupportedModels().includes(this.deviceModel);
    }

    get isSirenSupported(): boolean {
        return LosantCoreDevice.getSirenSupportedModels().includes(this.deviceModel);
    }

    get isVirtualIgnitionSupported(): boolean {
        return LosantCoreDevice.getVirtualIgnitionSupportedModels().includes(this.deviceModel);
    }

    get isFlightModeSupported(): boolean {
        return LosantCoreDevice.getFlightModeSupportedModels().includes(this.deviceModel);
    }

    get isLightSupported(): boolean {
        return LosantCoreDevice.getLightSupportedModels().includes(this.deviceModel);
    }

    get locationDeterminant(): ELocationDeterminant {
        return getLocationDeterminantFromString(this.getTagValue('location_determinant'));
    }

    /*
        Batteries right now are based on Maker/Manufacturer of device
        TODO: Figure out which devices have internal, external, both power supplies and work accordingly
     */
    get isInternalBatterySupported(): boolean {
        return LosantCoreDevice.getInternalBatteriesSupportedTypes()
            .includes(this.type);
    }

    get isBatteryComputedOnBackend(): boolean {
        return LosantCoreDevice.getDeviceTypesThatReportBothBatteryAndVoltage()
            .includes(this.type);
    }

    get isExternalBatterySupported(): boolean {
        return LosantCoreDevice.getExternalBatteriesSupportedTypes()
            .includes(this.type);
    }

    get isRecoveryModeSupported(): boolean {
        return LosantCoreDevice.getRecoverySupportedTypes().includes(this.type);
    }

    supports(feature: EHolotrakDeviceFeatures) {
        switch (feature) {
            case EHolotrakDeviceFeatures.flightSensor:
                return this.isFlightModeSupported;

            case EHolotrakDeviceFeatures.externalBattery:
                return this.isExternalBatterySupported;

            case EHolotrakDeviceFeatures.internalBattery:
                return this.isInternalBatterySupported;

            case EHolotrakDeviceFeatures.virtualIgnition:
                return this.isVirtualIgnitionSupported;

            case EHolotrakDeviceFeatures.doorSensor:
                return this.isSirenSupported;

            case EHolotrakDeviceFeatures.lightSensor:
                return this.isLightSupported;

            case EHolotrakDeviceFeatures.locationSensor:
                return this.isMappable;

            case EHolotrakDeviceFeatures.recovery:
                return this.isRecoveryModeSupported;

            case EHolotrakDeviceFeatures.basicInputOutput:
                return !this.matchDeviceModel(LosantCoreDevice.getModelsWithoutBasicIO());

            case EHolotrakDeviceFeatures.onDeviceGeofencing:
                return !this.type.includes('yabby');

            case EHolotrakDeviceFeatures.bluetoothAccessory:
                return this.matchDeviceModel(LosantCoreDevice.getModelsWithBluetoothAccessorySupport());

            case EHolotrakDeviceFeatures.welding_can:
                return this.matchDeviceModel(LosantCoreDevice.getModelsWithWeldingCanSupport())
                    || [
                        ESensorType.MILLER_CAN,
                        ESensorType.WELDING_CAN_ONE
                    ].includes(this.sensorType);

            case EHolotrakDeviceFeatures.diagnostics:
                return (
                    !this.matchDeviceModel(LosantCoreDevice.getModelsWithoutDiagnostics())
                    && !this.supports(EHolotrakDeviceFeatures.welding_can)
                );

            default:
                return false;
        }
    }

    /**
     * Get the value of a tag from the device tags
     *
     * using `device_type` allows getting the `device *type*` without any transformations
     *
     * for instance, `type` will return `Smart Tracker` for a device with `type` tag set to `calamp`
     *  but the original value 'calamp' is returned with `device_type`
     *  - for Yabby Edge the type itself is stored as Yabby Edge for some odd reason.
     *
     * this allows for a more consistent way of getting the device type when adding manufacturer-specific logic
     */
    getTagValue(name: string): string | null {
        if (typeof this.tags === 'string') {
            return null;
        }

        const qualifiedTagName = (name === 'device_type') ? 'type' : name;
        const value = this.tags?.find(tag => tag.key === qualifiedTagName)?.value || null;

        if (name === 'type' && value) {
            switch (`${value}`.toLowerCase()) {
                case 'calamp':
                    return 'Smart Tracker';

                case 'external-sensor':
                    return 'External';

                case 'atrack':
                    return 'ATrack';

                case 'morey':
                    return 'Morey';

                case 'queclink':
                    return 'Queclink';

                default:
                    return value;
            }
        }

        return value;
    }

    getAttribute(name: CompositeStateType): DeviceAttribute {
        if (typeof this.attributes === 'string') {
            return null;
        }

        return this.attributes?.find(atr => atr.name === name) ?? new DeviceAttribute();
    }

    updateState(message: MqttMessage) {
        if (!this.compositeState) {
            this.compositeState = CompositeStateParser.parse({});
        }

        const stateKeys = Object.keys(this.compositeState ?? {}) as CompositeStateType[];

        stateKeys.forEach(itemKey => {
            this.compositeState[itemKey] = (message[itemKey] === undefined || message[itemKey] === null)
                ? this.compositeState[itemKey]
                : CompositeStateItemSerializers[itemKey].parse({
                    time: message.last_reported_at || dayjs(),
                    value: message[itemKey]
                });
        });

        if (stateKeys.includes('location') || stateKeys.push('address')) {
            this.compositeState.last_reported_at = CompositeStateItemSerializers.last_location_updated_at.parse({
                time: dayjs(),
                value: dayjs()
            });
        }
    }

    matchDeviceModel(model: string | SupportedDeviceModel | Array<string | SupportedDeviceModel>, partial: boolean = false): boolean {
        if (Array.isArray(model)) {
            return model.some(m => this.matchDeviceModel(m, partial));
        } else {
            const baseCheck = this.deviceModel?.toLowerCase() === model;
            if (!partial || baseCheck) {
                return baseCheck;
            } else {
                return this.deviceModel
                    ?.toLowerCase()
                    .split('-')
                    .slice(0, 2)
                    .filter(p => !!p)
                    .join('-')
                    ?.includes(model.toLowerCase());
            }
        }
    }

    getCompositeStateItem(index: CompositeStateType): CompositeStateItem {
        return this.compositeState?.getItem(index);
    }


    // INFO: Use getCompositeBooleanValue for boolean responses
    getCompositeValue(index: CompositeStateType, suffix: string = '', fallback: string = '-', alwaysShowSuffix: boolean = true) {
        const item = this.getCompositeStateItem(index);

        if (!item?.hasValue() || this.shouldItemValueBeHidden(item, index)) {
            return (alwaysShowSuffix) ? fallback + ` ${suffix}` : fallback;
        }

        const itemValue = item.toString() + ` ${suffix}`;
        return `${itemValue}`.trim();
    }

    // FIXME: This is a hack to get the boolean value from the composite state. Because BooleanCompositeStateItem toString returns Yes/No
    getCompositeBooleanValue(index: CompositeStateType, fallback: boolean = false) {
        return this.getCompositeValue(index, '', '') === 'Yes' ?? fallback;
    }

    getCompositeCanDetail(index: CompositeStateType): ICANDetail {
        return {
            key: index,
            title: getCompositeItemTitle(index),
            value: this.getCompositeValue(index, getCompositeItemSuffix(index)),
            status: this.getCompositeItemStatus(index),
        };
    }

    getStateComputedValue(itemKey: SupportedComputableState): StateComputedValue {
        switch (itemKey) {
            case 'service_state':
                return {
                    title: 'Service State',
                    // TODO: Compute Service State
                    value: 'Okay!',
                    suffix: '',
                    type: "success"
                };


            case 'next_service':
                return {
                    title: 'Next Service',
                    // TODO: Compute Next Service based on device data and attribute configuration
                    value: '-',
                    suffix: 'Kms',
                    type: "danger"
                }

            case 'battery':
                return {
                    title: 'Battery',
                    value: this.getCompositeValue('battery_level'),
                    suffix: getCompositeItemSuffix('battery_level'),
                    type: null
                }

            case 'voltage':
                return {
                    title: 'Voltage',
                    value: this.getCompositeValue('voltage_level'),
                    suffix: getCompositeItemSuffix('voltage_level'),
                    type: null
                }

            case 'engine_hours':
                return {
                    title: 'Engine Hours',
                    value: this.getCompositeValue('engine_oil_maintenance_hours'),
                    suffix: getCompositeItemSuffix('engine_oil_maintenance_hours'),
                    type: null
                }

            case 'distance_travelled':
                return {
                    title: 'Total Distance',
                    // TODO: Compute Total Distance Travelled
                    value: '-',
                    suffix: 'miles',
                    type: null
                }

            case 'rpm':
                return {
                    title: 'Engine Speed',
                    value: this.getCompositeValue('rpm'),
                    suffix: getCompositeItemSuffix('rpm'),
                    type: null
                }

            case 'fuel':
                return {
                    title: 'Fuel Level',
                    value: this.getCompositeValue('fuel'),
                    suffix: 'L',
                    type: null
                }

            default:
                return {
                    title: `Unsupported Key ${itemKey}`,
                    value: '-',
                    suffix: null,
                    type: "warning"
                };
        }
    }

    hasBatteryValues(): boolean {
        // INFO: Here we are relying on direct access to battery_level so that we are sure of connection and report.
        const battery = this.compositeState?.getItem('battery_level');
        const voltage = this.compositeState?.getItem('voltage_level');
        const canRelyOnVoltageData = LosantCoreDevice.getDeviceTypesThatOnlyReportVoltage().includes(this.type) && voltage?.hasValue();
        return (battery?.hasValue() || canRelyOnVoltageData) && !!this.getDefiniteBatteryValues();
    }

    getExternalBatteryValues(): IFormattedBatteryValues {
        const compositeBatteryLevel = parseInt(this.getCompositeValue("battery_level"));
        const compositeVoltageLevel = parseFloat(this.getCompositeValue("voltage_level"));
        const onlyVoltageIsReported = LosantCoreDevice.getDeviceTypesThatOnlyReportVoltage().includes(this.type) || compositeBatteryLevel === 0;
        const isCharging = this.getCompositeBooleanValue('is_charging', false);

        const supportsExternalBattery = this.isExternalBatterySupported
            || (!this.isInternalBatterySupported && this.matchDeviceModel('barra-gps'));

        let voltage: number;

        if (compositeVoltageLevel >= 100) {
            // This usually happens when the voltage is in mV
            const integerPart = compositeVoltageLevel.toString().slice(0, 2);
            const decimalPart = compositeVoltageLevel.toString().slice(2);
            voltage = parseFloat(`${integerPart}.${decimalPart}`);
        } else if (onlyVoltageIsReported) {
            voltage = compositeVoltageLevel;
        } else {
            const integerPart = compositeVoltageLevel.toString().slice(0, 1);
            const decimalPart = compositeVoltageLevel.toString().slice(1);
            voltage = parseFloat(`${integerPart}.${decimalPart}`);
        }

        return formatBatteryValues({
            isCharging,
            voltage: supportsExternalBattery ? voltage : null,
            percentage: onlyVoltageIsReported
                ? calculateBatteryPercentageFromExternalVoltage(this.getCompositeValue("voltage_level"), this.getTagValue('device_model'))
                : compositeBatteryLevel
        })
    }

    getInternalBatteryValues(): IFormattedBatteryValues {
        const compositeBatteryLevel = parseInt(this.getCompositeValue("battery_level"));
        const compositeVoltageLevel = parseFloat(this.getCompositeValue("voltage_level"));

        const isCharging = this.getCompositeBooleanValue('is_charging', false);

        const deviceBatteryVoltage = this.isInternalBatterySupported
            ? compositeVoltageLevel
            : computeInternalBatteryVoltageFromLevel(compositeBatteryLevel);

        const internalBatteryLevel = this.isBatteryComputedOnBackend
            ? compositeBatteryLevel
            : calculateBatteryLevelFromInternalVoltage(deviceBatteryVoltage, this.getTagValue('device_model'));

        return formatBatteryValues({
            isCharging,
            percentage: internalBatteryLevel,
            voltage: deviceBatteryVoltage
        });
    }

    // TODO: Later on we will determine whether to use external or internal based on the device model.
    getDefiniteBatteryValues(): IFormattedBatteryValues {
        if (this.isInternalBatterySupported) {
            return this.getInternalBatteryValues();
        } else if (this.isExternalBatterySupported) {
            return this.getExternalBatteryValues();
        }
    }

    getComputedBatteryValues(): IBatteryValues {
        return {
            external: this.getExternalBatteryValues(),
            internal: this.getInternalBatteryValues(),
        };
    }

    // TODO: Have better computation of the composite item status
    getCompositeItemStatus(index: CompositeStateType): EDeviceStatus {
        return this.getCompositeStateItem(index).hasValue() ? EDeviceStatus.ONLINE : EDeviceStatus.OFFLINE;
    }

    shouldItemValueBeHidden(item: CompositeStateItem, index: CompositeStateType) {
        return (this.supports(EHolotrakDeviceFeatures.welding_can)) ? !item?.hasValidMillerValue(index) : false;
    }

    getTemperatureUnit(): ETemperatureUnit {
        let unit: string = '';
        try {
            unit = this.getAttribute('temperature')?.getUnit();
        } catch (exception) {
            console.error('Something went wrong', exception);
        }


        return convertStringToTemperatureUnit(unit);
    }

    getDateString(date: number | string | dayjs.Dayjs, fallback = '--'): string {
        if (date) {
            return dayjs(date).toISOString();
        }

        return fallback;
    }

    geofenceSlotSupported(slot: GeofenceSlotConfig): boolean {
        const geofencingLogic = getDeviceGeofencingLogic(this.getTagValue('geofencing_logic'));

        // FUTURE: Add support for device models
        return (geofencingLogic === EGeofencingLogic.ON_PLATFORM)
            ? true
            : (this.type && this.type === slot?.deviceMake.toLowerCase());
    }

    getCommand(commandType: EDeviceCommandTypes, type?: string): string | void {
        return undefined;
    }

    getAvailableAccessoryTypes() {
        if (this.supports(EHolotrakDeviceFeatures.bluetoothAccessory)) {
            switch (this.deviceModel) {
                case 'queclink-gv-57-mg':
                    return [
                        'sensor',
                        'relay',
                        'unset'
                    ];

                default:
                    return [];
            }
        } else {
            return [];
        }
    }


    static getDeviceTypesThatOnlyReportVoltage() {
        return [
            'calamp',
        ];
    }

    static getDeviceTypesThatReportBothBatteryAndVoltage() {
        return [
            'queclink',
            'yabby edge',
            'external-sensor'
        ]
    }

    static getInternalBatteriesSupportedTypes() {
        return [
            'atrack',
            'external-sensor',
            'yabby edge',
            'queclink',
        ];
    }

    static getExternalBatteriesSupportedTypes() {
        return [
            'calamp'
        ];
    }

    static getModelsWithWeldingCanSupport() {
        return [
            'queclink-gv-650-mg-can',
        ]
    }

    static getModelsWithoutDiagnostics() {
        return [
            'queclink-tmr-100-mg',
        ];
    }

    static getModelsWithoutBasicIO() {
        return [];
    }

    static getModelsWithBluetoothAccessorySupport() {
        return [
            'queclink-gv-57-mg',
        ];
    }

    static getAttributeConfigSupportedModels() {
        return [
            'holotrak-fill-sensor',
            'queclink-tmr-100-mg'
        ];
    }

    static getVirtualIgnitionSupportedModels() {
        return [
            'queclink-gv-57-mg',
        ];
    }

    static getSirenSupportedModels() {
        return [
            "queclink-gv-57-mg",
            "queclink-gv-door-sensor"
            // "queclink-gv-620-mg"
        ];
    }

    static getRecoverySupportedTypes() {
        return [
            'yabby edge'
        ];
    }

    static getLightSupportedModels() {
        return [
            'queclink-tmr-100-mg'
        ]
    }


    static getFlightModeSupportedModels() {
        return [
            'queclink-tmr-100-mg'
        ]
    }

    static parseTags(device: IHasTags): DeviceTag[] {
        return (typeof device.tags === 'string')
            ? DeviceTagSerializer?.parseAsArray(JSON.parse(device.tags))
            : device?.tags;
    }

    static parseAttributes(device: IHasAttributes): DeviceAttribute[] {
        if (Array.isArray(device.attributes)) {
            return DeviceAttributeSerializer.parseAsArray(device.attributes);
        } else {
            return (typeof device.attributes === 'string')
                ? DeviceAttributeSerializer?.parseAsArray(JSON.parse(device.attributes))
                : device?.attributes;
        }
    }
}


export const LosantCoreDeviceSerializer = new TypedJSON(LosantCoreDevice);
